import type {
  AdminAccount,
  AdminContentItem,
  AdminSessionItem,
  CountdownData,
  HeartDataResponse,
  HeartSettings,
  User,
} from './types';

const API_BASE = '/api';

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers = new Headers(options.headers || {});

  // Anti-CSRF protection header
  headers.set('X-Requested-With', 'XMLHttpRequest');

  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let errorMsg = `Request failed (${response.status})`;
    try {
      const data = (await response.json()) as { error?: string };
      if (data && typeof data.error === 'string') {
        errorMsg = data.error;
      }
    } catch {
      // non-JSON response body
    }
    throw new ApiError(errorMsg, response.status);
  }

  return response.json() as Promise<T>;
}

export const api = {
  // Public Countdown
  async getCountdown(): Promise<CountdownData> {
    return request<CountdownData>('/countdown', {
      method: 'GET',
    });
  },

  // Authentication
  async login(email: string, password: string): Promise<{ user: User }> {
    return request<{ user: User }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async logout(): Promise<{ success: boolean }> {
    return request<{ success: boolean }>('/logout', {
      method: 'POST',
    });
  },

  async getSession(): Promise<{ authenticated: boolean; user?: User }> {
    return request<{ authenticated: boolean; user?: User }>('/session', {
      method: 'GET',
    });
  },

  // Heart Content
  async getHeartData(): Promise<HeartDataResponse> {
    return request<HeartDataResponse>('/heart', {
      method: 'GET',
    });
  },

  // Admin Settings
  async getAdminSettings(): Promise<HeartSettings> {
    return request<HeartSettings>('/admin/settings', {
      method: 'GET',
    });
  },

  async updateAdminSettings(settings: Partial<HeartSettings>): Promise<{ success: boolean; settings: HeartSettings }> {
    return request<{ success: boolean; settings: HeartSettings }>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },

  // Admin Accounts
  async getAdminAccounts(): Promise<{ accounts: AdminAccount[] }> {
    return request<{ accounts: AdminAccount[] }>('/admin/accounts', {
      method: 'GET',
    });
  },

  async updateAdminAccount(data: {
    accountId: string;
    email?: string;
    newPassword?: string;
    isActive?: boolean;
  }): Promise<{ success: boolean }> {
    return request<{ success: boolean }>('/admin/accounts', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Admin Content
  async getAdminContent(): Promise<{
    lovePhrases: AdminContentItem[];
    nicknames: AdminContentItem[];
  }> {
    return request<{
      lovePhrases: AdminContentItem[];
      nicknames: AdminContentItem[];
    }>('/admin/content', {
      method: 'GET',
    });
  },

  async createAdminContent(data: {
    type: 'love' | 'nickname';
    text: string;
    language?: string;
  }): Promise<{ success: boolean; id: string }> {
    return request<{ success: boolean; id: string }>('/admin/content', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAdminContent(data: {
    type: 'love' | 'nickname';
    id: string;
    text?: string;
    language?: string;
    isActive?: boolean;
  }): Promise<{ success: boolean }> {
    return request<{ success: boolean }>('/admin/content', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteAdminContent(type: 'love' | 'nickname', id: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/admin/content?type=${type}&id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // Admin Import & Export
  async importAdminContent(data: {
    type: 'csv_phrases' | 'markdown_nicknames';
    content: string;
  }): Promise<{ success: boolean; importedCount: number; skippedCount: number }> {
    return request<{ success: boolean; importedCount: number; skippedCount: number }>('/admin/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getExportUrl(type: 'csv_phrases' | 'markdown_nicknames' | 'json_settings'): string {
    return `${API_BASE}/admin/export?type=${type}`;
  },

  // Admin Sessions
  async getAdminSessions(): Promise<{ sessions: AdminSessionItem[] }> {
    return request<{ sessions: AdminSessionItem[] }>('/admin/sessions', {
      method: 'GET',
    });
  },

  async revokeAdminSession(sessionId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/admin/sessions?id=${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    });
  },

  async revokeAllAdminSessions(keepCurrent = true): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/admin/sessions?all=true&keepCurrent=${keepCurrent}`, {
      method: 'DELETE',
    });
  },

  // Database Maintenance & Optimization
  async optimizeDatabase(): Promise<{ success: boolean; message: string }> {
    return request<{ success: boolean; message: string }>('/admin/maintenance', {
      method: 'POST',
    });
  },
};
